import React, { useState, useEffect } from 'react';
import { emailAPI } from '../services/api';
import './EmailHistorySection.css';

const EmailHistorySection = ({ memberId, memberName, memberEmail }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedEmails, setExpandedEmails] = useState(new Set());

  useEffect(() => {
    if (memberId) {
      loadEmailHistory();
    }
  }, [memberId, currentPage]);

  const loadEmailHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await emailAPI.getMemberEmailHistory(memberId, {
        page: currentPage,
        limit: 10
      });

      const { emails: emailData, pagination } = response.data.data;
      setEmails(emailData);
      setTotalPages(pagination.pages);
    } catch (err) {
      console.error('Error loading email history:', err);
      setError(err.response?.data?.error || 'Failed to load email history');
    } finally {
      setLoading(false);
    }
  };

  const toggleEmailExpanded = (emailId) => {
    const newExpanded = new Set(expandedEmails);
    if (newExpanded.has(emailId)) {
      newExpanded.delete(emailId);
    } else {
      newExpanded.add(emailId);
    }
    setExpandedEmails(newExpanded);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'sent': return 'status-badge sent';
      case 'delivered': return 'status-badge delivered';
      case 'opened': return 'status-badge opened';
      case 'clicked': return 'status-badge clicked';
      case 'bounced': return 'status-badge bounced';
      case 'failed': return 'status-badge failed';
      default: return 'status-badge';
    }
  };

  if (loading && emails.length === 0) {
    return (
      <div className="email-history-section">
        <h3>📧 Email History</h3>
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading email history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="email-history-section">
        <h3>📧 Email History</h3>
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={loadEmailHistory} className="btn btn-outline">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="email-history-section">
      <div className="section-header">
        <h3>📧 Email History</h3>
        <div className="section-info">
          <span className="email-count">{emails.length} email{emails.length !== 1 ? 's' : ''}</span>
          {memberEmail && (
            <span className="recipient-email">to {memberEmail}</span>
          )}
        </div>
      </div>

      {emails.length === 0 ? (
        <div className="no-emails">
          <div className="no-emails-icon">📭</div>
          <h4>No emails sent yet</h4>
          <p>No email history found for {memberName || 'this member'}.</p>
        </div>
      ) : (
        <>
          <div className="email-list">
            {emails.map((email) => (
              <div key={email._id} className="email-item">
                <div className="email-header">
                  <div className="email-basic-info">
                    <div className="email-subject">
                      <strong>{email.subject}</strong>
                    </div>
                    <div className="email-meta">
                      <span className="email-template">
                        Template: {email.templateName}
                      </span>
                      <span className="email-date">
                        {formatDate(email.sentAt)}
                      </span>
                      <span className={getStatusBadgeClass(email.status)}>
                        {email.status}
                      </span>
                    </div>
                  </div>
                  <button
                    className="expand-toggle"
                    onClick={() => toggleEmailExpanded(email._id)}
                    title={expandedEmails.has(email._id) ? 'Show less' : 'Show more'}
                  >
                    {expandedEmails.has(email._id) ? '−' : '+'}
                  </button>
                </div>

                {expandedEmails.has(email._id) && (
                  <div className="email-details">
                    <div className="email-info-grid">
                      <div className="info-item">
                        <label>Message ID:</label>
                        <span className="message-id">{email.messageId || 'N/A'}</span>
                      </div>
                      <div className="info-item">
                        <label>Delivery Status:</label>
                        <span className={`delivery-status ${email.deliveryStatus || 'unknown'}`}>
                          {email.deliveryStatus || 'Unknown'}
                        </span>
                      </div>
                      <div className="info-item">
                        <label>Provider:</label>
                        <span className="provider">{email.provider || 'Gmail'}</span>
                      </div>
                      <div className="info-item">
                        <label>Sent By:</label>
                        <span>{email.sentBy || 'System'}</span>
                      </div>
                    </div>

                    {email.deliveredAt && (
                      <div className="delivery-info">
                        <label>Delivered:</label>
                        <span>{formatDate(email.deliveredAt)}</span>
                      </div>
                    )}

                    {email.openedAt && (
                      <div className="opened-info">
                        <label>First Opened:</label>
                        <span>{formatDate(email.openedAt)}</span>
                        {email.opens && email.opens.length > 1 && (
                          <span className="open-count">
                            ({email.opens.length} times)
                          </span>
                        )}
                      </div>
                    )}

                    {email.clicks && email.clicks.length > 0 && (
                      <div className="clicks-info">
                        <label>Clicks:</label>
                        <span>{email.clicks.length} link{email.clicks.length !== 1 ? 's' : ''} clicked</span>
                      </div>
                    )}

                    {email.variables && Object.keys(email.variables).length > 0 && (
                      <div className="variables-info">
                        <label>Template Variables Used:</label>
                        <div className="variables-list">
                          {Object.entries(email.variables).map(([key, value]) => (
                            <div key={key} className="variable-item">
                              <span className="variable-key">{key}:</span>
                              <span className="variable-value">{value || 'N/A'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {email.deliveryError && (
                      <div className="error-info">
                        <label>Error:</label>
                        <span className="error-text">{email.deliveryError}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="btn btn-outline"
              >
                ← Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="btn btn-outline"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmailHistorySection;